import {
  BitlyPreference,
  BulkUpdatePostInput,
  CreateLinkInput,
  CreatePostInput,
  GetPostsEditorQuery,
  LinkDestination,
  LinkGoogleCallToAction,
  PostExpiryInput,
  PostGroupInput,
  PostInputStatus,
  PostMediaInput,
  PostMultiMediaAttachMode,
  PostPublishMethod,
  PostQueuePosition,
  PostRecurringInterval,
  PostStatus,
  PostTiming,
  PostTimingInput,
  UtmInput
} from '../../../models/graphql';
import {
  GroupPostAttributesContentType,
  GroupPostAttributesSchedulingType,
  GroupPostAttributesType,
  MediaTypePostEditorState,
  PostEditorLink,
  PostEditorRecurringIntervalType,
  PostEditorState
} from '../../../models/post-editor';
import { serializeText } from '../TextEditor/helpers/serializer';
import {
  mapTikTokProfileValuesToPostTikTokInput,
  SocialProfileWithBoardsAndMentionType
} from './mapSelectedProfilesValues';
import { GraphQLError } from 'graphql';
import { mediaContainsVideo } from './mediaContainsVideo';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { getExistingUtmKeys, IsKeyOfUtmInput, makeFinalLink, UtmInputKeys } from '../states/reducers/link.reducer';
import { getUrlParams } from '../../../helpers/getUrlParams';
import { canAddComment } from './canAddComment';

// js version of LinkMetadataType.php isBitlink
export const isBitly = (url: string): boolean => {
  const urlObject = getUrlParams(url);
  if (!urlObject) return false;
  return ['bitly.com', 'bit.ly', 'j.mp'].includes(urlObject.hostname);
};

export const getInitialUtm = (link: PostEditorLink | null): Partial<UtmInput> => {
  if (!link) return {};
  const actualExistingLink =
    link.linkMetadata?.expandedUrl && link.destination === LinkDestination.EXPANDED
      ? link.linkMetadata.expandedUrl
      : link.initialUrl;
  const existingUtmKeys = getExistingUtmKeys(actualExistingLink);
  if (!existingUtmKeys.length) return {};
  const { url: _, ...baseSettings } = link;
  const destinationUrl = makeFinalLink(baseSettings, false);
  const initialLinkUrl = getUrlParams(actualExistingLink);
  const finalLinkUrl = getUrlParams(destinationUrl);
  if (!initialLinkUrl || !finalLinkUrl) return {};
  const { searchParams: initialSearchParams } = initialLinkUrl;
  // const { searchParams: finalSearchParams } = finalLinkUrl;
  let urlKeys: string[] = [];
  const utmKeys = UtmInputKeys.map(k => `utm_${k}`);
  for (const key of Array.from(initialSearchParams.keys())) {
    if (utmKeys.includes(key)) {
      urlKeys = [...urlKeys, key];
    }
  }
  if (!urlKeys.length) return {};
  return urlKeys.reduce((prev, current) => {
    const strippedCurrent = current.replace('utm_', '');
    if (!IsKeyOfUtmInput(strippedCurrent)) return prev;
    const currentValue = initialSearchParams.get(current);
    return {
      ...prev,
      ...(currentValue ? { [strippedCurrent]: currentValue } : {})
    };
  }, {});
};

const getLink = (
  input: PostEditorLink | null,
  profile: SocialProfileWithBoardsAndMentionType
): CreateLinkInput | null => {
  if (input === null) return null;
  // we need to destruct object in order to remove displayUrl prop. delete prop doesn't work due to TS constraints
  const { url: _, linkMetadata: __, addUtm, removeUtm, useBitly, ...result } = input;
  const { utmTracking } = result;
  let utm: UtmInput = {
    campaign: utmTracking && addUtm ? utmTracking.campaign : '',
    content: utmTracking && addUtm ? utmTracking.content : '',
    medium: utmTracking && addUtm ? utmTracking.medium : '',
    source: utmTracking && addUtm ? utmTracking.source : '',
    term: utmTracking && addUtm ? utmTracking.term : ''
  };
  if (!removeUtm) {
    const mergableUtm = getInitialUtm(input);
    utm = { ...utm, ...mergableUtm };
  }
  result.googleCallToAction =
    profile.__typename === 'GoogleSocialProfile' ? result.googleCallToAction : LinkGoogleCallToAction.NONE;
  result.utmTracking = utm;
  return { ...result, bitlyPreference: useBitly ? BitlyPreference.DEFAULT : BitlyPreference.OFF };
};
const getInterval = (interval: PostEditorRecurringIntervalType): PostRecurringInterval => {
  switch (interval) {
    case PostEditorRecurringIntervalType.days:
      return PostRecurringInterval.DAY;
    case PostEditorRecurringIntervalType.weeks:
      return PostRecurringInterval.WEEK;
    case PostEditorRecurringIntervalType.years:
      return PostRecurringInterval.YEAR;
  }
};

const getPostId = (post: GroupPostAttributesType): string => {
  if (!post.postId) {
    throw new GraphQLError('Post ID missing');
  }
  return post.postId;
};
const getPostTiming = (post: GroupPostAttributesType, defaultQueuePosition: PostQueuePosition): PostTimingInput => {
  const { scheduling, postStatus } = post;
  const { postTiming } = scheduling;
  switch (postTiming) {
    case PostTiming.NOW:
      // If we have post timing set to Now and post is draft, we need to set it to queue, since we can't both draft and post now
      return postStatus !== PostStatus.DRAFT
        ? {
            type: PostTiming.NOW
          }
        : { type: PostTiming.QUEUE, queue: { queuePosition: defaultQueuePosition } };
    case PostTiming.QUEUE:
      const queuePosition = scheduling.queuePosition === 'CURRENT' ? null : scheduling.queuePosition;
      return {
        type: PostTiming.QUEUE,
        queue: {
          queuePosition
          // if quePosition in Edit mode is CURRENT, we set it to null, and back-end will leave it as it is
        }
      };
    case PostTiming.SCHEDULE:
      return {
        type: PostTiming.SCHEDULE,
        schedule: {
          localDatetimes: scheduling.scheduledLocalDateTimes.filter(t => !t.isPast).map(dt => dt.dateTime),
          timezone: scheduling.scheduledTimezone
        }
      };
    case PostTiming.RECURRING:
      if (!scheduling.recurringType) throw new Error('Scheduling recurring type is null! We cant get the interval!');
      if (typeof scheduling.recurringValue !== 'number') throw new Error('Scheduling recurring value is not a number!');
      return {
        type: PostTiming.RECURRING,
        recurring: {
          localDatetime: scheduling.recurring.date,
          interval: getInterval(scheduling.recurringType),
          // we cast as here since validation has passed and interval != null
          /** The timezone for the recurring post. If left null, will use Workspace default */
          timezone: scheduling.scheduledTimezone,
          value: scheduling.recurringValue
          // we cast as here since validation has passed and interval != null
        }
      };
  }
};
const getPostExpiry = (scheduling: GroupPostAttributesSchedulingType): PostExpiryInput | null => {
  const expiry = scheduling.recurring.active
    ? scheduling.recurring
    : scheduling.evergreen.active
    ? scheduling.evergreen
    : null;
  if (expiry === null) return null;
  if (!expiry.expireType.length) return null;
  let expiryDate: PostExpiryInput['localDate'];
  let maxRepeats: PostExpiryInput['maxRepeats'];
  if (expiry.expireType.includes('date')) {
    expiryDate = expiry.expiryDate ?? undefined;
  }
  if (expiry.expireType.includes('count')) {
    maxRepeats = expiry.expiryMaxRepeats ?? undefined;
  }
  return {
    localDate: expiryDate,
    maxRepeats
  };
};

const getMedia = (media: MediaTypePostEditorState[]): PostMediaInput[] | null =>
  media.map(item => (item.externalUpload ? { url: item.url, type: item.type } : { mediaId: item.id, type: item.type }));

const getMultiMediaAttachMode = (
  profile: SocialProfileWithBoardsAndMentionType,
  content: GroupPostAttributesContentType
): PostMultiMediaAttachMode | null => {
  if (!profile.__typename) {
    throw new GraphQLError('Social Profile missing typename.');
  }
  const { varyMedia, media } = content;
  // If no media or only 1 media is present, we should use null as a value since no MULTI / ALTERNATE can be applied
  if (media.length < 2) {
    return null;
  }
  switch (profile.__typename) {
    case 'GoogleSocialProfile':
    case 'PinterestSocialProfile':
      return PostMultiMediaAttachMode.ALTERNATE;
    default:
      return varyMedia || null;
  }
};

const getPostStatus = (status: PostStatus): PostInputStatus => {
  if (status === PostStatus.ACTIVE || status === PostStatus.DRAFT) return PostInputStatus[status];
  // TODO throw an error in it's not ACTIVE | DRAFT | TRASHED
  return PostInputStatus.TRASH;
};

const getFirstComment = (
  content: GroupPostAttributesContentType,
  profile: SocialProfileWithBoardsAndMentionType
): string | null => {
  if (!profile.__typename) {
    throw new GraphQLError('Social Profile missing typename.');
  }
  if (content.shouldShowFirstComment && canAddComment([profile], content.postFeedType)) {
    return serializeText(content.firstComment) || null;
  }
  return null;
};

const getTitle = (
  profile: SocialProfileWithBoardsAndMentionType,
  content: GroupPostAttributesContentType
): string | null => {
  const { media, title } = content;
  if (!profile.__typename) {
    throw new GraphQLError('Social Profile missing typename.');
  }
  if (profile.__typename === 'PinterestSocialProfile') {
    return title || null;
  } else if (['FacebookSocialProfile', 'FacebookSocialProfile', 'TwitterSocialProfile'].includes(profile.__typename)) {
    return mediaContainsVideo(media) ? title || null : null;
  }
  return null;
};

const getIgPublishingMethod = (
  profile: SocialProfileWithBoardsAndMentionType,
  content: GroupPostAttributesContentType
): PostPublishMethod | null => (profile.__typename !== 'InstagramSocialProfile' ? null : content.publishMethod);

const getProfileWithProps = (
  profiles: SocialProfileWithBoardsAndMentionType[],
  profile: SelectedSocialProfileValue
): SocialProfileWithBoardsAndMentionType => {
  const detailedProfile = profiles.find(p => p.id === profile.profileId);
  if (!detailedProfile) {
    throw new GraphQLError('Mapping profile failed.');
  }
  return detailedProfile;
};
export const mapStateToPostInput = (
  state: PostEditorState,
  profiles: SocialProfileWithBoardsAndMentionType[],
  defaultQueuePosition: PostQueuePosition,
  fetchedFamily?: GetPostsEditorQuery['latestFamilyMembersOfPost']
): { updatePosts: BulkUpdatePostInput[]; createPosts: PostGroupInput[] | null; deletePosts: string[] } => {
  let createPosts: PostGroupInput[] | null = null;
  let updatePosts: BulkUpdatePostInput[] = [];
  let deletePosts: string[] = [];
  const filteresGroups = state.postGroups.filter(g => !g.deleted);
  filteresGroups.forEach(postGroup => {
    const customizeContent = postGroup.customizeContentPerProfile;
    const customizeSchedule = postGroup.customizeSchedulePerProfile;
    const { groupPostAttributes, posts: customizedPosts } = postGroup;
    if (!postGroup.categoryId) {
      throw new GraphQLError('Mapping profile failed due to illegal post category.');
    }
    if (!customizeSchedule && !customizeContent) {
      const postSource = groupPostAttributes.postSource;
      if (!postSource) {
        throw new GraphQLError('Mapping profile failed due to illegal post source.');
      }
      const createPost: PostGroupInput = {
        categoryId: postGroup.categoryId,
        familyUuid: postGroup.familyUuid,
        posts: postGroup.profiles.map<CreatePostInput>(profile => {
          const detailedProfile = getProfileWithProps(profiles, profile);
          return {
            socialProfileId: profile.profileId,
            timing: getPostTiming(groupPostAttributes, defaultQueuePosition),
            status: getPostStatus(groupPostAttributes.postStatus),
            title: getTitle(detailedProfile, groupPostAttributes.content),
            texts: groupPostAttributes.content.texts.map(({ text }) => serializeText(text)),
            firstComment: getFirstComment(groupPostAttributes.content, detailedProfile), // or null if we don't support it,
            evergreen: groupPostAttributes.scheduling.evergreen.active,
            expiry: getPostExpiry(groupPostAttributes.scheduling),
            media: getMedia(groupPostAttributes.content.media),
            multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, groupPostAttributes.content),
            pinterestBoard: profile.pnBoardId || null,
            link: getLink(groupPostAttributes.content.link, detailedProfile),
            feedType: profile.__typename === 'InstagramSocialProfile' ? groupPostAttributes.content.postFeedType : null,
            instagramPreferredPublishMethod:
              profile.__typename === 'InstagramSocialProfile'
                ? getIgPublishingMethod(detailedProfile, groupPostAttributes.content)
                : null,
            tikTok:
              profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                : null,
            src: postSource,
            ...(groupPostAttributes.parentId ? { parentId: groupPostAttributes.parentId } : {})
          };
        })
      };
      createPosts = createPosts ? [...createPosts, createPost] : [createPost];
    } else if (!customizeSchedule && customizeContent) {
      const createPost: PostGroupInput = {
        categoryId: postGroup.categoryId,
        familyUuid: postGroup.familyUuid,
        posts: postGroup.profiles.map<CreatePostInput>(profile => {
          const detailedProfile = getProfileWithProps(profiles, profile);
          const currentPost = customizedPosts.find(post => post.customizedProfileId === profile.profileId);
          if (!currentPost)
            throw new Error(
              "We can't find the current post! post.customizedProfileId and profile.profileId and not equal"
            );
          const currentPostSource = currentPost.postSource;
          if (!currentPostSource) throw new GraphQLError('Mapping profile failed due to illegal post source.');
          return {
            socialProfileId: profile.profileId,
            timing: getPostTiming(groupPostAttributes, defaultQueuePosition),
            status: getPostStatus(currentPost.postStatus),
            title: getTitle(detailedProfile, currentPost.content),
            texts: currentPost.content.texts.map(({ text }) => serializeText(text)),
            firstComment: getFirstComment(currentPost.content, detailedProfile), // or null if we don't support it,
            evergreen: groupPostAttributes.scheduling.evergreen.active,
            expiry: getPostExpiry(groupPostAttributes.scheduling),
            media: getMedia(currentPost.content.media),
            multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, currentPost.content),
            pinterestBoard: profile.pnBoardId || null,
            link: getLink(currentPost.content.link, detailedProfile),
            feedType: profile.__typename === 'InstagramSocialProfile' ? currentPost.content.postFeedType : null,
            instagramPreferredPublishMethod:
              profile.__typename === 'InstagramSocialProfile' ? currentPost.content.publishMethod : null,
            tikTok:
              profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                : null,
            src: currentPostSource,
            ...(currentPost.parentId ? { parentId: currentPost.parentId } : {})
          };
        })
      };
      createPosts = createPosts ? [...createPosts, createPost] : [createPost];
    } else if (customizeSchedule && !customizeContent) {
      const createPost: PostGroupInput = {
        categoryId: postGroup.categoryId,
        familyUuid: postGroup.familyUuid,
        posts: postGroup.profiles.map<CreatePostInput>(profile => {
          const detailedProfile = getProfileWithProps(profiles, profile);
          const currentPost = customizedPosts.find(post => post.customizedProfileId === profile.profileId);
          if (!currentPost)
            throw new Error(
              "We can't find the current post! post.customizedProfileId and profile.profileId and not equal"
            );
          const currentPostSource = currentPost.postSource;
          if (!currentPostSource) throw new GraphQLError("Post source is not set! we can't map the post.");
          return {
            socialProfileId: profile.profileId,
            timing: getPostTiming(currentPost, defaultQueuePosition),
            status: getPostStatus(currentPost.postStatus),
            title: getTitle(detailedProfile, groupPostAttributes.content),
            texts: groupPostAttributes.content.texts.map(({ text }) => serializeText(text)),
            firstComment: getFirstComment(groupPostAttributes.content, detailedProfile), // or null if we don't support it,
            evergreen: currentPost.scheduling.evergreen.active,
            expiry: getPostExpiry(currentPost.scheduling),
            media: getMedia(groupPostAttributes.content.media),
            multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, groupPostAttributes.content),
            pinterestBoard: profile.pnBoardId || null,
            link: getLink(groupPostAttributes.content.link, detailedProfile),
            feedType: profile.__typename === 'InstagramSocialProfile' ? groupPostAttributes.content.postFeedType : null,
            instagramPreferredPublishMethod:
              profile.__typename === 'InstagramSocialProfile'
                ? getIgPublishingMethod(detailedProfile, groupPostAttributes.content)
                : null,
            tikTok:
              profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                : null,
            src: currentPostSource,
            ...(currentPost.parentId ? { parentId: currentPost.parentId } : {})
          };
        })
      };
      createPosts = createPosts ? [...createPosts, createPost] : [createPost];
    } else if (customizeSchedule && customizeContent) {
      // this is the only state where we edit a post. this means we can add profiles from siblings and we must make sure post src is set
      const activeProfilesIds = postGroup.posts.flatMap(post =>
        post.customizedProfileId ? post.customizedProfileId : []
      );
      const pastPostsProfileIds = postGroup.readOnlyPosts.flatMap(post =>
        post.postStatus === PostStatus.PUBLISHED && post.customizedProfileId ? post.customizedProfileId : []
      );
      const createFromPastPosts: CreatePostInput[] = postGroup.profiles.flatMap<CreatePostInput>(profile => {
        const detailedProfile = getProfileWithProps(profiles, profile);
        const currentPost = customizedPosts.find(post => post.customizedProfileId === profile.profileId);
        if (!currentPost)
          throw new Error(
            "We can't find the current post! post.customizedProfileId and profile.profileId and not equal"
          );
        const pastPost = postGroup.readOnlyPosts.find(post => post.customizedProfileId === profile.profileId);
        if (
          pastPostsProfileIds.includes(profile.profileId) &&
          pastPost &&
          postGroup.familyUuid &&
          !currentPost.postSource
        ) {
          throw new GraphQLError("Post source is not set! we can't map the post.");
        }
        return pastPostsProfileIds.includes(profile.profileId) &&
          pastPost &&
          postGroup.familyUuid &&
          currentPost.postSource
          ? {
              parentId: currentPost.parentId,
              socialProfileId: profile.profileId,
              timing: getPostTiming(currentPost, defaultQueuePosition),
              status: getPostStatus(currentPost.postStatus),
              title: getTitle(detailedProfile, currentPost.content),
              texts: currentPost.content.texts.map(({ text }) => serializeText(text)),
              firstComment: getFirstComment(currentPost.content, detailedProfile), // or null if we don't support it,
              evergreen: currentPost.scheduling.evergreen.active,
              expiry: getPostExpiry(currentPost.scheduling),
              media: getMedia(currentPost.content.media),
              multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, currentPost.content),
              pinterestBoard: profile.pnBoardId || null,
              link: getLink(currentPost.content.link, detailedProfile),
              feedType: profile.__typename === 'InstagramSocialProfile' ? currentPost.content.postFeedType : null,
              instagramPreferredPublishMethod:
                profile.__typename === 'InstagramSocialProfile' ? currentPost.content.publishMethod : null,
              tikTok:
                profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                  ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                  : null,
              src: currentPost.postSource
            }
          : [];
      });
      const createNewPostFromScratch: CreatePostInput[] = postGroup.profiles.flatMap(profile => {
        const detailedProfile = getProfileWithProps(profiles, profile);
        const currentPost = customizedPosts.find(post => post.customizedProfileId === profile.profileId);
        if (!currentPost)
          throw new Error(
            "We can't find the current post! post.customizedProfileId and profile.profileId and not equal"
          );
        const isInFetchedPosts = !!fetchedFamily?.find(post => post.socialProfile.id === profile.profileId);
        const pastPost = postGroup.readOnlyPosts.find(post => post.customizedProfileId === profile.profileId);
        const currentPostSource = currentPost.postSource;
        if (!currentPostSource && !pastPostsProfileIds.includes(profile.profileId) && !pastPost && !isInFetchedPosts)
          throw new GraphQLError("Post source is not set! we can't map the post.");
        return !pastPostsProfileIds.includes(profile.profileId) && !pastPost && !isInFetchedPosts && currentPostSource
          ? {
              socialProfileId: profile.profileId,
              timing: getPostTiming(currentPost, defaultQueuePosition),
              status: getPostStatus(currentPost.postStatus),
              title: getTitle(detailedProfile, currentPost.content),
              texts: currentPost.content.texts.map(({ text }) => serializeText(text)),
              firstComment: getFirstComment(currentPost.content, detailedProfile), // or null if we don't support it,
              evergreen: currentPost.scheduling.evergreen.active,
              expiry: getPostExpiry(currentPost.scheduling),
              media: getMedia(currentPost.content.media),
              multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, currentPost.content),
              pinterestBoard: profile.pnBoardId || null,
              link: getLink(currentPost.content.link, detailedProfile),
              feedType: profile.__typename === 'InstagramSocialProfile' ? currentPost.content.postFeedType : null,
              instagramPreferredPublishMethod:
                profile.__typename === 'InstagramSocialProfile' ? currentPost.content.publishMethod : null,
              tikTok:
                profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                  ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                  : null,
              src: currentPostSource,
              ...(currentPost.parentId ? { parentId: currentPost.parentId } : {})
            }
          : [];
      });
      const updates = postGroup.profiles.flatMap<{ post: BulkUpdatePostInput; profileId: string | null }>(profile => {
        const detailedProfile = getProfileWithProps(profiles, profile);
        const currentPost = customizedPosts.find(post => post.customizedProfileId === profile.profileId && post.postId);
        return !pastPostsProfileIds.includes(profile.profileId) && currentPost
          ? {
              post: {
                postId: getPostId(currentPost),
                postInput: {
                  categoryId: postGroup.categoryId,
                  evergreen: currentPost.scheduling.evergreen.active,
                  expiry: getPostExpiry(currentPost.scheduling),
                  feedType:
                    profile.__typename === 'InstagramSocialProfile' ? currentPost.content.postFeedType : undefined,
                  firstComment: getFirstComment(currentPost.content, detailedProfile),
                  link: getLink(currentPost.content.link, detailedProfile),
                  media: getMedia(currentPost.content.media),
                  multiMediaAttachMode: getMultiMediaAttachMode(detailedProfile, currentPost.content),
                  pinterestBoard: profile.pnBoardId || undefined,
                  status: getPostStatus(currentPost.postStatus),
                  texts: currentPost.content.texts.map(({ text }) => serializeText(text)),
                  timing: getPostTiming(currentPost, defaultQueuePosition),
                  title: getTitle(detailedProfile, currentPost.content),
                  instagramPreferredPublishMethod:
                    profile.__typename === 'InstagramSocialProfile' ? currentPost.content.publishMethod : null,
                  tikTok:
                    profile.__typename === 'TikTokSocialProfile' && profile.tikTok
                      ? mapTikTokProfileValuesToPostTikTokInput(profile.tikTok)
                      : null
                }
              },
              profileId: currentPost.customizedProfileId
            }
          : [];
      });
      updatePosts = updates.map(item => item.post);
      // we should delete posts if they are present in the fetchedFamily -> social profile, they are active or draft,
      // but they are not in selected profiles
      deletePosts =
        fetchedFamily
          ?.filter(
            post =>
              post.socialProfile &&
              [PostStatus.ACTIVE, PostStatus.DRAFT].includes(post.status) &&
              !activeProfilesIds.includes(post.socialProfile.id)
          )
          .flatMap(item => item.id || []) || [];
      createPosts = createPosts
        ? [
            ...createPosts,
            {
              categoryId: postGroup.categoryId,
              ...(postGroup.familyUuid ? { familyUuid: postGroup.familyUuid } : {}),
              posts: [...createNewPostFromScratch, ...createFromPastPosts]
            }
          ]
        : createNewPostFromScratch.length || createFromPastPosts.length
        ? [
            {
              categoryId: postGroup.categoryId,
              ...(postGroup.familyUuid ? { familyUuid: postGroup.familyUuid } : {}),
              posts: [...createNewPostFromScratch, ...createFromPastPosts]
            }
          ]
        : null;
    }
  });

  return {
    createPosts,
    updatePosts,
    deletePosts
  };
};
